/////////////////////////////////////////////////////////////////////////////////////////////////////////
// 화일명 : comptMapUtil.tsx
// 컴포넌트 매핑 유틸리티
//  - componentMap : 동적 import를 위한 컴포넌트 매핑 :
//  - LoadingComponent : 로딩 컴포넌트
//  - ErrorComponent : 오류 컴포넌트(404 페이지를 찾을 수 없음)
//  - NoAuthComponent / PgmAuthGate : 권한 없음(403) 페이지 / 화면·팝업을 열기 전 권한 확인(2026-09-28)
//  - ComponentRenderer : 컴포넌트 렌더러
/////////////////////////////////////////////////////////////////////////////////////////////////////////
import React, { lazy, Suspense } from 'react';
import { useCommonTranslation } from '../translations';
import { checkPgmAuth, isPgmId, type PgmAuthResult } from './pgmAuthUtil'; // 화면/팝업 열기 전 권한 확인
import { PopupCloseContext } from '../components/ModalContext'; // 팝업 안에서 자체 [닫기]를 그릴 때 제목줄 [닫기]를 숨기라고 알림
import '../styles/pageNotFound.css'; // 페이지를 찾을 수 없음 안내 화면 스타일

// 팝업 안(403/404 등)에서 자체 [닫기] 버튼을 그릴 때 호출 - NaviMenuBar가 하는 것과 동일한 패턴(2026-09-30).
// 팝업이 아니라 메인 탭(closeTabId만 있고 PopupCloseContext.Provider가 없음)이면 reportInnerClose가 null이라 그냥 아무 일도 안 함.
function useReportInnerClose(active: boolean) {
  const reportInnerClose = React.useContext(PopupCloseContext);
  React.useEffect(() => {
    if (!reportInnerClose || !active) return;
    reportInnerClose(true);
    return () => reportInnerClose(false);
  }, [reportInnerClose, active]);
}
    
////////////////////////////////////////////////////////////////////////////////////////////////////////////
// 사용자정의 모듈
// DB 메뉴(url = "module/pgmId")를 기반으로 동적 import
//  - 파일 규칙: ../pages/modules/{module}/{PGMID}.tsx (PGMID는 대문자)
//  - 규칙에서 벗어나는 예외(여러 메뉴가 화면 하나를 공유하는 경우 등)만 componentAliasMap에 등록
////////////////////////////////////////////////////////////////////////////////////////////////////////////
const componentAliasMap: Record<string, () => Promise<{ default: React.ComponentType<any> }>> = {
  'za/za9002m1': () => import('../pages/modules/za/X7001M1'),  // 비밀번호변경 - 개인정보변경 화면 공유
  'za/za9003m1': () => import('../pages/modules/za/X7001M1'),  // 환경설정 - 개인정보변경 화면 공유
  'za/z2902q1': () => import('../pages/modules/za/Z2901M1'),   // 도움말조회 - 도움말등록 화면 공유(callPgmId로 조회전용 판별, 2026-09-30)
  // 2026-09-16: 패턴등록(생명/일반/자동차)은 alias맵 대신 ZA_MENU.CALL_PGM_ID를 아예 'sd/T1101M1'로
  // 등록해서 표준 규칙(module/PGMID -> 파일경로)만으로 해결함(메뉴등록 화면에서 관리) - alias 불필요.
};

// baseUrl("module/pgmId")당 lazy 컴포넌트를 한 번만 생성해서 재사용
//  (매 렌더마다 lazy()를 새로 만들면 매번 다른 컴포넌트 타입이 되어 리마운트/무한 로딩이 발생함)
const lazyComponentCache = new Map<string, React.LazyExoticComponent<React.ComponentType<any>>>();

export const resolveComponent = (baseUrl: string): React.LazyExoticComponent<React.ComponentType<any>> => {
  const key = baseUrl.toLowerCase();
  const cached = lazyComponentCache.get(key);
  if (cached) return cached;

  const [moduleId, pgmId] = key.split('/');
  const loader = componentAliasMap[key]
    ?? (() => import(`../pages/modules/${moduleId}/${(pgmId || '').toUpperCase()}`));

  // 동적 import 실패(해당 화면이 아직 없음/코드 불일치) 시 에러 화면으로 대체
  const Component = lazy(() =>
    loader().catch(() => ({ default: () => <ErrorComponent url={baseUrl} /> }))
  );
  lazyComponentCache.set(key, Component);
  return Component;
};

////////////////////////////////////////////////////////////////////////////////////////////////////////////
// 홈 화면 컴포넌트 렌더러
//  - 홈의 호출프로그램ID(ZA_USER_HOME.CALL_PGM_ID 또는 ZA_MENU(LINK_KEY='HOME').CALL_PGM_ID)가 곧 pages/{ID}.tsx 파일명임
//    (예: HomePageU1 -> pages/HomePageU1.tsx). 별도 등록부 없이 새 홈 화면은 HomePage로 시작하는 파일만 만들고 DB에 ID를 넣으면 됨.
//  - 보안/번들 범위: ID는 HomePage로 시작하는 영숫자만 허용하고, webpackInclude로 pages/HomePage*.tsx만 번들 대상에 포함함.
//  - 파일이 없거나 ID가 규칙에 맞지 않으면 "페이지를 찾을 수 없습니다" 화면을 보여줌.
////////////////////////////////////////////////////////////////////////////////////////////////////////////
const homeComponentCache = new Map<string, React.LazyExoticComponent<React.ComponentType<any>>>();

const resolveHomeComponent = (pgmId: string): React.LazyExoticComponent<React.ComponentType<any>> => {
  const cached = homeComponentCache.get(pgmId);
  if (cached) return cached;
  const validId = /^HomePage[A-Za-z0-9]*$/.test(pgmId);
  const Component = lazy(() =>
    (validId
      ? import(/* webpackInclude: /[\\/]HomePage[A-Za-z0-9]*\.tsx$/ */ `../pages/${pgmId}`)
      : Promise.reject(new Error('invalid home id'))
    ).catch(() => ({ default: () => <ErrorComponent url={pgmId || 'HOME'} /> }))
  );
  homeComponentCache.set(pgmId, Component);
  return Component;
};

// 홈 로딩 표시 - 잠깐(기본 350ms) 안에 끝나는 로딩은 아무것도 안 보여줘서 "로딩 중" 문구가 번쩍 나타났다 사라지는 깜빡임을 막음
export const DelayedLoading: React.FC<{ delay?: number }> = ({ delay = 350 }) => {
  const [show, setShow] = React.useState(false);
  React.useEffect(() => {
    const t = window.setTimeout(() => setShow(true), delay);
    return () => window.clearTimeout(t);
  }, [delay]);
  return show ? <LoadingComponent /> : null;
};

export const HomeRenderer: React.FC<{ pgmId: string; homeProps?: Record<string, any> }> = ({ pgmId, homeProps }) => {
  const Component = resolveHomeComponent(pgmId);
  return (
    <Suspense fallback={<DelayedLoading />}>
      <Component {...homeProps} />
    </Suspense>
  );
};

////////////////////////////////////////////////////////////////////////////////////////////////////////////
// URL이 React 컴포넌트인지 확인하는 함수
//  현재 미사용 - 동적 import 방식에서는 실제로 로드해봐야 존재 여부를 알 수 있어 형식만 검사
////////////////////////////////////////////////////////////////////////////////////////////////////////////
export const isReactComponent = (url: string): boolean => {
  const baseUrl = url.split('?')[0];
  return baseUrl.split('/').length === 2;
};

////////////////////////////////////////////////////////////////////////////////////////////////////////////
// 컴포넌트 렌더러
//  props: url = 호출할 페이지, paramData = 쿼리스트링 데이터(json타입), autoQuery = 자동조회여부,
//         menuId = 이 탭의 실제 ZA_MENU.MENU_ID(mainFrame의 tab.id) - 각 화면이 useCommonTranslation()에
//         넘겨서 DD(ZA_DD) 조회를 MENU_ID 기준으로 스코프하는 데 씀(2026-09-18, PGM_ID -> MENU_ID 전환)
////////////////////////////////////////////////////////////////////////////////////////////////////////////
export const ComponentRenderer: React.FC<{ url: string, paramData: any, autoQuery: any, menuId?: string }> = ({ url, paramData, autoQuery, menuId }) => {
  // 쿼리스트링 제거 (예: sd/SD9002Q1?data=... → sd/SD9002Q1)
  const baseUrl = url.split('?')[0];
  if (!baseUrl || baseUrl.split('/').length !== 2) {
    return <ErrorComponent url={url} />;
  }
  const Component = resolveComponent(baseUrl);

  return (
    <PgmAuthGate pgmId={baseUrl.split('/')[1]} closeTabId={baseUrl}>
      <Suspense fallback={<LoadingComponent />}>
        <Component  url={url} callPgmId={baseUrl} paramData={paramData} autoQuery={autoQuery} menuId={menuId} />
      </Suspense>
    </PgmAuthGate>
  );
};

////////////////////////////////////////////////////////////////////////////////////////////////////////////
// 권한 확인 게이트 - 화면 탭(ComponentRenderer)과 팝업(CommonModal)이 열리기 전에 권한을 먼저 확인
//  - DENIED(메뉴·팝업에 등록돼 있으나 사용자 권한그룹에 없음) : 화면 대신 403 페이지
//  - MODULE_DENIED(권한은 있지만 회사가 사지 않은 모듈, LICENSE_MODE=SAAS) : 화면 대신 라이선스 안내 403 페이지 (2026-09-30)
//  - OK / NOT_REGISTERED(미등록 - 검사 대상 아님) : 그대로 화면을 그림(화면 파일이 없으면 기존 404 페이지)
//  - 확인하는 동안은 아무것도 그리지 않음(권한 없는 화면이 잠깐 보였다 사라지지 않게)
//  - closeTabId : 탭이면 닫을 탭ID, onClose : 팝업이면 팝업 닫기 함수
////////////////////////////////////////////////////////////////////////////////////////////////////////////
export const PgmAuthGate: React.FC<{ pgmId?: string; closeTabId?: string; onClose?: () => void; children: React.ReactNode }> = ({ pgmId, closeTabId, onClose, children }) => {
  const pgm = String(pgmId || '').toUpperCase();
  const [auth, setAuth] = React.useState<PgmAuthResult | null>(isPgmId(pgm) ? null : 'NOT_REGISTERED');
  const [retry, setRetry] = React.useState(0);   // 403 화면의 [다시 확인] - 권한 부여 후 탭을 닫지 않고 다시 확인
  React.useEffect(() => {
    if (!isPgmId(pgm)) { setAuth('NOT_REGISTERED'); return; }
    let alive = true;
    setAuth(null);
    checkPgmAuth(pgm).then(r => { if (alive) setAuth(r); });
    return () => { alive = false; };
  }, [pgm, retry]);
  if (auth === null) return null;
  if (auth === 'DENIED') return <NoAuthComponent pgmId={pgm} closeTabId={closeTabId} onClose={onClose} onRetry={() => setRetry(n => n + 1)} />;
  if (auth === 'MODULE_DENIED') return <NoModuleComponent pgmId={pgm} closeTabId={closeTabId} onClose={onClose} onRetry={() => setRetry(n => n + 1)} />;
  return <>{children}</>;
};

////////////////////////////////////////////////////////////////////////////////////////////////////////////
// 로딩 컴포넌트
////////////////////////////////////////////////////////////////////////////////////////////////////////////
const LoadingComponent: React.FC = () => {
  const { ddKey } = useCommonTranslation(); // 훅을 컴포넌트 내부에서 호출
  return (
    <div className="flex items-center justify-center h-64">
      <div className="text-center">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <p className="mt-2 text-gray-600">{ddKey('msgLoadingPage') || '페이지를 로딩 중입니다...'}</p>
      </div>
    </div>
  );
};
////////////////////////////////////////////////////////////////////////////////////////////////////////////
// 오류 컴포넌트
////////////////////////////////////////////////////////////////////////////////////////////////////////////
export const ErrorComponent: React.FC<{ url: string }> = ({ url }) => {
  const { ddKey } = useCommonTranslation(); // 훅을 컴포넌트 내부에서 호출
  const tabId = (url || '').split('?')[0];   // 탭 식별키(callPgmId=tab.url) - 좁은 모바일 화면에는 탭 닫기 버튼이 없어서 여기서 직접 닫을 수 있게 함
  useReportInnerClose(true);   // 항상 자체 [닫기]를 그리므로, 팝업 안이면 제목줄 [닫기]는 숨김(2026-09-30)
  return (
    <div className="pnf-wrap pnf-404">
      <div className="pnf-card">
        <div className="pnf-icon"><i className="fas fa-compass"></i></div>
        <div className="pnf-code">404</div>
        <h3 className="pnf-title">{ddKey('msgPageNotFound') || '페이지를 찾을 수 없습니다'}</h3>
        <p className="pnf-desc">
          {ddKey('msgPageNotFoundDetail', { url }) || '요청하신 페이지가 존재하지 않거나 아직 준비되지 않았습니다.'}
        </p>
        {tabId && <span className="pnf-url" title={tabId}>{tabId}</span>}
        <div className="pnf-actions">
          <button type="button" className="pnf-btn primary" onClick={() => window.closeTab?.(tabId)}>
            <i className="fas fa-xmark"></i>{ddKey('btnCloseTab') || '닫기'}
          </button>
        </div>
      </div>
    </div>
  );
};

////////////////////////////////////////////////////////////////////////////////////////////////////////////
// 권한 없음(403) 컴포넌트 - 404(ErrorComponent)와 같은 모양, 아이콘/코드/문구만 다름
//  - 권한 없는 화면·팝업도 메뉴/우클릭 항목은 그대로 보이고, 열면 이 페이지가 나옴(고객 요청으로 권한 부여 - 사용자 결정 2026-09-28)
////////////////////////////////////////////////////////////////////////////////////////////////////////////
export const NoAuthComponent: React.FC<{ pgmId: string; closeTabId?: string; onClose?: () => void; onRetry?: () => void }> = ({ pgmId, closeTabId, onClose, onRetry }) => {
  const { ddKey } = useCommonTranslation();
  const handleClose = () => { if (onClose) onClose(); else if (closeTabId) window.closeTab?.(closeTabId); };
  useReportInnerClose(!!(onClose || closeTabId));   // 자체 [닫기]를 그리면, 팝업 안이면 제목줄 [닫기]는 숨김(2026-09-30)
  return (
    <div className="pnf-wrap pnf-403">
      <div className="pnf-card">
        <div className="pnf-icon"><i className="fas fa-shield-halved"></i></div>
        <div className="pnf-code">403</div>
        <h3 className="pnf-title">{ddKey('msgNoAuth') || '이 화면을 사용할 권한이 없습니다'}</h3>
        <p className="pnf-desc">
          {ddKey('msgNoAuthDetail') || '관리자에게 이 화면(팝업)의 권한을 요청하세요.'}
        </p>
        {pgmId && <span className="pnf-url" title={pgmId}>{pgmId}</span>}
        {(onRetry || onClose || closeTabId) && (
          <div className="pnf-actions">
            {onRetry && (
              <button type="button" className="pnf-btn" onClick={onRetry}>
                <i className="fas fa-rotate-right"></i>{ddKey('btnRecheck') || '다시 확인'}
              </button>
            )}
            {(onClose || closeTabId) && (
              <button type="button" className="pnf-btn primary" onClick={handleClose}>
                <i className="fas fa-xmark"></i>{ddKey('btnCloseTab') || '닫기'}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

////////////////////////////////////////////////////////////////////////////////////////////////////////////
// 라이선스에 없는 모듈(403) 컴포넌트 - NoAuthComponent와 같은 모양, 아이콘/코드/문구만 다름 (2026-09-30)
//  - 라이선스가 바뀐 뒤 새로고침 안 한 옛 세션이 사이드메뉴에 아직 남아있는 항목을 클릭했을 때 뜸
////////////////////////////////////////////////////////////////////////////////////////////////////////////
export const NoModuleComponent: React.FC<{ pgmId: string; closeTabId?: string; onClose?: () => void; onRetry?: () => void }> = ({ pgmId, closeTabId, onClose, onRetry }) => {
  const { ddKey } = useCommonTranslation();
  const handleClose = () => { if (onClose) onClose(); else if (closeTabId) window.closeTab?.(closeTabId); };
  useReportInnerClose(!!(onClose || closeTabId));   // 자체 [닫기]를 그리면, 팝업 안이면 제목줄 [닫기]는 숨김(2026-09-30)
  return (
    <div className="pnf-wrap pnf-403">
      <div className="pnf-card">
        <div className="pnf-icon"><i className="fas fa-lock"></i></div>
        <div className="pnf-code">403</div>
        <h3 className="pnf-title">{ddKey('msgNoModule') || '구매하지 않은 모듈의 화면입니다'}</h3>
        <p className="pnf-desc">
          {ddKey('msgNoModuleDetail') || '관리자에게 문의하세요.'}
        </p>
        {pgmId && <span className="pnf-url" title={pgmId}>{pgmId}</span>}
        {(onRetry || onClose || closeTabId) && (
          <div className="pnf-actions">
            {onRetry && (
              <button type="button" className="pnf-btn" onClick={onRetry}>
                <i className="fas fa-rotate-right"></i>{ddKey('btnRecheck') || '다시 확인'}
              </button>
            )}
            {(onClose || closeTabId) && (
              <button type="button" className="pnf-btn primary" onClick={handleClose}>
                <i className="fas fa-xmark"></i>{ddKey('btnCloseTab') || '닫기'}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
